import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useCustomHotkeys } from '../../../../common/hooks/useCustomHotkeys';
import { EditorMode } from '../../../../modules/editor/models/EditorModes';
import { AnnotationConfirmationModal, AnnotationConfirmationModalMode } from '../../../components/AnnotationConfirmationModal';
import { SaveBeforeRejectConfirmationModal } from '../../../components/SaveBeforeRejectConfirmationModal';
import { useLoader } from '../../../helpers/useLoader';
import { useAnnotationPermissions } from '../annotation.permissions';
import { IAnnotationService } from '../annotation.service';
import { IAnnotationReviewService } from '../annotationReview.service';
import { CorrectNavigation } from '../components/CorrectNavigation';
import { IFreeDrawSegmentationService } from '../freeDrawSegmentation.service';
import { IAnnotationTypeBl } from '../submodules/annotationTypes/annotationType.bl';
import { IReviewRejectReasonsStore } from '../submodules/reviewRejectReasones/reviewRejectReasons.store';
import { IUndoRedoHistory } from '../undoRedoHistory.service';

export const CorrectControlsContainer = observer((): ReactElement => {
  const annotationReviewService = useInjection(IAnnotationReviewService.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const historyService = useInjection(IUndoRedoHistory.$);
  const reviewRejectReasonsStore = useInjection(IReviewRejectReasonsStore.$);
  const annotationService = useInjection(IAnnotationService.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const permissions = useAnnotationPermissions();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAnnotationConfirmationModal, setShowAnnotationConfirmationModal] = useState(false);
  const [annotationConfirmationModalMode, setAnnotationConfirmationModalMode] = useState(AnnotationConfirmationModalMode.SUBMIT);
  const [showDiscardConfirmationModal, setShowDiscardConfirmationModal] = useState(false);
  const [showSaveBeforeRejectConfirmationModal, setShowSaveBeforeRejectConfirmationModal] = useState(false);

  const { isLoading, withLoaderAsync } = useLoader();

  const handleSubmitAndAcceptAsync = async (): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    await withLoaderAsync(async () => annotationReviewService.correctAcceptAnnotationAsync(), 'submit-and-accept-button');
  };

  const handleReject = async (): Promise<void> => {
    if (!annotationService.validateAnnotations()) {
      await annotationTypeBl.handleReject();
      return;
    }

    setShowSaveBeforeRejectConfirmationModal(false);
    toggleAnnotationConfirmationModal(AnnotationConfirmationModalMode.REJECT);
  };

  const handleConfirmReject = async (reason?: string): Promise<void> => {
    setShowAnnotationConfirmationModal(false);
    if (reason) {
      await handleRejectConfirmed(reason);
    } else {
      setShowSaveBeforeRejectConfirmationModal(true);
    }
  };

  const handleRejectConfirmed = async (reason?: string): Promise<void> => {
    setShowSaveBeforeRejectConfirmationModal(false);
    await withLoaderAsync(async () => annotationReviewService.correctRejectAnnotationAsync(reason), 'reject-button');
  };

  const handleRejectCanceled = (): void => {
    setShowSaveBeforeRejectConfirmationModal(false);
    setShowAnnotationConfirmationModal(false);
  };

  const handleSaveBeforeRejectConfirmed = async (): Promise<void> => {
    await handleRejectConfirmed();
  };

  const handleCancelAnnotationConfirm = async (): Promise<void> => {
    await annotationReviewService.cancelCorrectAsync();
    setShowCancelModal(false);
  };

  const handleCancelAnnotationCancel = (): void => {
    setShowCancelModal(false);
  };

  const handleCancelAnnotation = async (): Promise<void> => {
    if (!historyService.canUndo) {
      await annotationReviewService.cancelCorrectAsync();
    } else {
      setShowCancelModal(true);
    }
  };

  const handleDiscardConfirmed = async (): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async () => annotationReviewService.correctDiscardAnnotationAsync(), 'discard-annotation-button');
  };

  const handleDiscardCanceled = (): void => {
    setShowDiscardConfirmationModal(false);
  };

  const handleDiscard = (): void => {
    setShowDiscardConfirmationModal(true);
  };

  const toggleAnnotationConfirmationModal = (mode?: AnnotationConfirmationModalMode): void => {
    if (mode !== undefined) {
      setAnnotationConfirmationModalMode(mode);
    }
    setShowAnnotationConfirmationModal((prev) => !prev);
  };

  const disabled = isLoading || freeDrawSegmentationService.freeDrawInProgress || freeDrawSegmentationService.freeDrawFeature !== undefined;

  useCustomHotkeys(
    'ctrl+shift+enter',
    (): void => {
      setAnnotationConfirmationModalMode(AnnotationConfirmationModalMode.SUBMITANDACCEPT);
      toggleAnnotationConfirmationModal();
    },
    { enabled: !disabled },
    [setAnnotationConfirmationModalMode, toggleAnnotationConfirmationModal]
  );

  useCustomHotkeys(
    'ctrl+shift+backspace',
    (): void => {
      setAnnotationConfirmationModalMode(AnnotationConfirmationModalMode.REJECT);
      toggleAnnotationConfirmationModal();
    },
    { enabled: !disabled },
    [setAnnotationConfirmationModalMode, toggleAnnotationConfirmationModal]
  );

  return (
    <>
      <CorrectNavigation
        disabled={disabled}
        canDiscard={permissions.canDiscard().allowed}
        editorMode={EditorMode.BATCH_REVIEWEDIT}
        onSubmitAndAccept={handleSubmitAndAcceptAsync}
        onReject={handleReject}
        onCancel={handleCancelAnnotation}
        onConfirmCancel={handleCancelAnnotationConfirm}
        onCancelCancel={handleCancelAnnotationCancel}
        showCancelModal={showCancelModal}
        showDiscardModal={showDiscardConfirmationModal}
        onDiscardCanceled={handleDiscardCanceled}
        onDiscardConfirmed={handleDiscardConfirmed}
        onDiscard={handleDiscard}
      />
      <SaveBeforeRejectConfirmationModal
        onSaveBeforeRejectCancel={handleRejectCanceled}
        onSaveBeforeRejectConfirm={handleSaveBeforeRejectConfirmed}
        onSaveBeforeRejectDecline={handleReject}
        showSaveBeforeRejectModal={showSaveBeforeRejectConfirmationModal}
      />
      <AnnotationConfirmationModal
        showAnnotationConfirmationModal={showAnnotationConfirmationModal}
        annotationConfirmationModalMode={annotationConfirmationModalMode}
        rejectAutoCompleteOptions={reviewRejectReasonsStore.rejectionReasons.slice()}
        onAnnotationConfirm={
          annotationConfirmationModalMode === AnnotationConfirmationModalMode.SUBMITANDACCEPT ? handleSubmitAndAcceptAsync : handleConfirmReject
        }
        onAnnotationCancel={toggleAnnotationConfirmationModal}
      />
    </>
  );
});
